import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import TabItem from "./TabItem";
import { TabPanelProps } from "./UserProfileTabs";
import LayerChecklist from "./LayerChecklist";
import FeatureChecklist from "./FeatureChecklist";
import { AdminApi, RolePermission } from "../../../services/adminApi";
import { mockLayers } from "../../../global/constants/layers";
import Typography from "../../../Components/Typography";

// Role display names in Portuguese
const ROLE_LABELS: Record<string, string> = {
  viewer: "Visualizador",
  specialist: "Especialista",
  admin: "Administrador",
};

export default function LayerPermissionsTab(props: TabPanelProps) {
  const { value, index } = props;

  const [roles, setRoles] = useState<RolePermission[]>([]);
  const [selectedRoleName, setSelectedRoleName] = useState<string | null>(null);
  const [availableFeatures, setAvailableFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the currently selected role object
  const selectedRole = useMemo(
    () => roles.find((r) => r.role_name === selectedRoleName) || null,
    [roles, selectedRoleName]
  );

  // Fetch roles and features
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [rolesData, featuresData] = await Promise.all([
        AdminApi.getRolePermissions(),
        AdminApi.getAvailableFeatures(),
      ]);

      setRoles(rolesData);
      setAvailableFeatures(featuresData);

      // Auto-select first role if none selected
      if (rolesData.length > 0) {
        setSelectedRoleName((current) => current ?? rolesData[0].role_name);
      }
    } catch (err: any) {
      console.error("Error fetching permissions data:", err);
      setError(err.message || "Erro ao carregar dados de permissões");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data when tab is active
  useEffect(() => {
    if (value === index) {
      fetchData();
    }
  }, [value, index, fetchData]);

  // Handle role selection change
  const handleRoleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newRole: string | null
  ) => {
    if (newRole !== null) {
      setSelectedRoleName(newRole);
    }
  };

  // Handle save layers
  const handleSaveLayers = async (layers: string[]) => {
    if (!selectedRoleName) throw new Error("Nenhum papel selecionado");

    await AdminApi.updateRolePermission(selectedRoleName, {
      allowed_layers: layers,
    });

    // Update local state
    setRoles((prev) =>
      prev.map((r) =>
        r.role_name === selectedRoleName ? { ...r, allowed_layers: layers } : r
      )
    );
  };

  // Handle save features
  const handleSaveFeatures = async (features: string[]) => {
    if (!selectedRoleName) throw new Error("Nenhum papel selecionado");

    await AdminApi.updateRolePermission(selectedRoleName, {
      allowed_features: features,
    });

    // Update local state
    setRoles((prev) =>
      prev.map((r) =>
        r.role_name === selectedRoleName
          ? { ...r, allowed_features: features }
          : r
      )
    );
  };

  // Get role label for display
  const getRoleLabel = (roleName: string) => {
    return ROLE_LABELS[roleName] || roleName;
  };

  // Get all available layer names
  const allLayerNames = useMemo(
    () => mockLayers.map((layer) => layer.layerName),
    []
  );

  // Get effective selected layers (expand wildcard to all layers)
  const effectiveSelectedLayers = useMemo(() => {
    if (!selectedRole) return [];
    if (selectedRole.allowed_layers.includes("*")) {
      return allLayerNames;
    }
    return selectedRole.allowed_layers;
  }, [selectedRole, allLayerNames]);

  // Render loading state
  if (loading && roles.length === 0) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
      >
        {value === index && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </div>
    );
  }

  const layerPermissionsItem = {
    title: "Camadas",
    description: `Configure as camadas acessíveis para o papel "${getRoleLabel(
      selectedRoleName || ""
    )}"`,
    content: error ? (
      <Alert severity="error">{error}</Alert>
    ) : !selectedRole ? (
      <Alert severity="info">Selecione um papel para configurar</Alert>
    ) : (
      <LayerChecklist
        selectedLayers={effectiveSelectedLayers}
        onSave={handleSaveLayers}
        loading={loading}
      />
    ),
  };

  const featurePermissionsItem = {
    title: "Funcionalidades",
    description: `Configure as funcionalidades disponíveis para o papel "${getRoleLabel(
      selectedRoleName || ""
    )}"`,
    content: error ? (
      <Alert severity="error">{error}</Alert>
    ) : !selectedRole ? (
      <Alert severity="info">Selecione um papel para configurar</Alert>
    ) : (
      <FeatureChecklist
        selectedFeatures={
          selectedRole.allowed_features > availableFeatures
            ? availableFeatures
            : selectedRole.allowed_features
        }
        availableFeatures={availableFeatures}
        onSave={handleSaveFeatures}
        loading={loading}
      />
    ),
  };

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {/* Role Selector */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" className="mb-2">
              Selecione o Papel
            </Typography>
            <ToggleButtonGroup
              value={selectedRoleName}
              exclusive
              onChange={handleRoleChange}
              aria-label="seleção de papel"
            >
              {roles.map((role) => (
                <ToggleButton
                  key={role.role_name}
                  value={role.role_name}
                  aria-label={role.role_name}
                  sx={{ px: 3 }}
                >
                  {getRoleLabel(role.role_name)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {selectedRole?.description && (
              <Typography variant="body4" className="text-[#676A70] mt-1">
                {selectedRole.description}
              </Typography>
            )}
          </Box>

          {/* Layers Section */}
          <Box sx={{ mb: 4 }}>
            <TabItem {...layerPermissionsItem} />
          </Box>

          {/* Features Section */}
          <Box>
            <TabItem {...featurePermissionsItem} />
          </Box>
        </Box>
      )}
    </div>
  );
}
